import { Loader2, Pencil, X } from 'lucide-react';
import type { SyntheticEvent } from 'react';
import { cn } from '@/lib/infra/utils';

interface OrderActionButtonsProps {
  disabled?: boolean;
  canceling?: boolean;
  onEdit: () => void;
  onCancel: () => void | Promise<void>;
  className?: string;
  /** Hide edit when only cancellation is proven safe. */
  editAvailable?: boolean;
}

export function OrderActionButtons({
  disabled = false,
  canceling = false,
  onEdit,
  onCancel,
  className,
  editAvailable = true,
}: OrderActionButtonsProps) {
  const stop = (e: SyntheticEvent) => {
    e.stopPropagation();
  };

  // #241: `canceling` outlives the DELETE — it stays true until an authoritative
  // Open Orders snapshot drops the ID. Keeping the "Cancel order" name through
  // that window reads as actionable on a control that is deliberately inert.
  const cancelLabel = canceling
    ? 'Cancel pending — waiting for Open Orders'
    : 'Cancel order';

  return (
    <div
      className={cn('inline-flex items-center gap-1', className)}
      onClick={stop}
      onPointerDown={stop}
    >
      {editAvailable && <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        disabled={disabled || canceling}
        title="Edit price"
        aria-label="Edit price"
        className="text-muted-foreground hover:bg-muted hover:text-foreground inline-flex h-6 w-6 items-center justify-center rounded transition-colors disabled:opacity-50"
      >
        <Pencil size={13} />
      </button>}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          void onCancel();
        }}
        disabled={disabled || canceling}
        title={cancelLabel}
        aria-label={cancelLabel}
        className="text-muted-foreground hover:bg-red-500/10 hover:text-red-400 inline-flex h-6 w-6 items-center justify-center rounded transition-colors disabled:opacity-50"
      >
        {canceling ? <Loader2 size={13} className="animate-spin" /> : <X size={14} />}
      </button>
    </div>
  );
}

export default OrderActionButtons;
